import { useCallback, useState } from 'react';
import type { BBox } from '@blackboard/shared';
import type { StrokeIndex } from '../canvas/spatialIndex';
import { screenToWorld, type Viewport } from '../canvas/viewport';

/**
 * Admin "erase a rectangular region" interaction.
 *
 * State machine: idle → selecting → pending → idle (or cancelled).
 *
 *   - idle:       not in admin-erase mode
 *   - selecting:  user is dragging out the rectangle (startScreen/endScreen present)
 *   - pending:    drag finished; bbox + estimated count ready for confirm dialog
 *
 * Owns its own fetch so res.ok / errors can be surfaced to the caller via onResult.
 */
export type AdminEraseState =
  | { kind: 'idle' }
  | { kind: 'selecting'; startScreen: [number, number]; endScreen: [number, number] | null }
  | { kind: 'pending'; bbox: BBox; count: number };

export interface AdminEraseResult {
  ok: boolean;
  erasedCount?: number;
  /** 服务端审计事件 id —— 用于 Ctrl+Z 撤销这次擦除 */
  eventId?: number;
  errorMessage?: string;
}

const MIN_DIM = 4;

interface Options {
  boardId: number | undefined;
  token: string;
  index: StrokeIndex;
  onResult?: (r: AdminEraseResult) => void;
}

export function useAdminAreaErase({ boardId, token, index, onResult }: Options) {
  const [state, setState] = useState<AdminEraseState>({ kind: 'idle' });

  const enter = useCallback(() => {
    setState({ kind: 'selecting', startScreen: [0, 0], endScreen: null });
  }, []);

  // Track that the next pointer-down should anchor the selection (not the artificial 0,0).
  const beginAt = useCallback((sx: number, sy: number) => {
    setState((s) => (s.kind === 'selecting' ? { kind: 'selecting', startScreen: [sx, sy], endScreen: null } : s));
  }, []);

  const updateAt = useCallback((sx: number, sy: number) => {
    setState((s) => (s.kind === 'selecting' ? { ...s, endScreen: [sx, sy] } : s));
  }, []);

  /** Finalize the drag: compute world bbox + local count; transition to pending or idle. */
  const finalize = useCallback(
    (viewport: Viewport) => {
      setState((s) => {
        if (s.kind !== 'selecting' || !s.endScreen) return { kind: 'idle' };
        const [x1s, y1s] = s.startScreen;
        const [x2s, y2s] = s.endScreen;
        const [wx1, wy1] = screenToWorld(viewport, Math.min(x1s, x2s), Math.min(y1s, y2s));
        const [wx2, wy2] = screenToWorld(viewport, Math.max(x1s, x2s), Math.max(y1s, y2s));
        if (Math.abs(wx2 - wx1) < MIN_DIM || Math.abs(wy2 - wy1) < MIN_DIM) {
          return { kind: 'idle' };
        }
        const bbox: BBox = { x1: wx1, y1: wy1, x2: wx2, y2: wy2 };
        const count = index.search(bbox).length;
        return { kind: 'pending', bbox, count };
      });
    },
    [index]
  );

  const cancel = useCallback(() => setState({ kind: 'idle' }), []);

  const confirm = useCallback(async () => {
    let toErase: BBox | null = null;
    setState((s) => {
      if (s.kind === 'pending') toErase = s.bbox;
      return s;
    });
    if (!toErase || boardId === undefined) {
      setState({ kind: 'idle' });
      onResult?.({ ok: false, errorMessage: 'no_pending' });
      return;
    }
    let res: Response;
    try {
      res = await fetch('/api/admin/erase/area', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ board_id: boardId, bbox: toErase }),
      });
    } catch {
      setState({ kind: 'idle' });
      onResult?.({ ok: false, errorMessage: '网络异常，请重试。' });
      return;
    }
    setState({ kind: 'idle' });
    if (!res.ok) {
      let msg = `服务端错误（${res.status}）`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error === 'erase_area_too_large') msg = '选区过大，请缩小后重试。';
        else if (body.error) msg = `操作失败：${body.error}`;
      } catch {}
      onResult?.({ ok: false, errorMessage: msg });
      return;
    }
    let count: number | undefined;
    let eventId: number | undefined;
    try {
      const body = (await res.json()) as { erased_count?: number; event_id?: number };
      count = body.erased_count;
      eventId = body.event_id;
    } catch {}
    onResult?.({ ok: true, erasedCount: count, eventId });
  }, [boardId, token, onResult]);

  return { state, enter, beginAt, updateAt, finalize, confirm, cancel };
}
